import { shallowEqual, useSelector } from 'react-redux';
import { ANALYTICS_FIELD_MAP } from '../constants';
import { hasBadAddress, selectAddressValidation } from '../selectors';
import { isPendingTransaction } from '../util/transactions';

/**
 * Everything AddressValidationView used to receive from connect(), as a hook.
 *
 * PORT NOTE: shallowEqual is required on selectAddressValidation. It returns
 * `state.vapService?.addressValidation || {}`, so an unpopulated store hands
 * back a fresh object on every call and the default reference check would
 * re-render on every dispatched action. connect() absorbed that with its own
 * shallow comparison of the merged props.
 *
 * PORT NOTE: mapStateToProps also supplied `formAppData` (`state.form.data`),
 * and mapDispatchToProps supplied `closeModal`, `resetAddressValidation` and
 * `setDataAction`. All four were declared in propTypes but never read or called
 * anywhere in the class body, so none of them are carried over.
 *
 * @param {object} params
 * @param {object} [params.transaction] the in-flight transaction, passed down
 *   by the field controller rather than read from the store.
 */
export const useAddressValidationData = ({ transaction } = {}) => {
  const {
    addressFromUser,
    addressValidationError,
    addressValidationErrorCode,
    addressValidationType,
    confirmedSuggestions,
    selectedAddress,
    selectedAddressId,
    suggestedAddresses,
    overrideValidationKey,
  } = useSelector(selectAddressValidation, shallowEqual);

  const userHasBadAddress = useSelector(hasBadAddress);

  const isFieldTransactionPending = useSelector(
    state =>
      state.vapService?.fieldTransactionMap?.[addressValidationType]
        ?.isPending || false,
  );

  const isNoValidationKeyAlertEnabled = useSelector(
    state => state.featureToggles?.profileShowNoValidationKeyAddressAlert,
  );

  const vapServiceFormFields = useSelector(
    state => state.vapService?.formFields,
  );

  return {
    addressFromUser,
    addressValidationError,
    addressValidationErrorCode,
    addressValidationType,
    analyticsSectionName: ANALYTICS_FIELD_MAP[addressValidationType],
    confirmedSuggestions,
    isLoading: isFieldTransactionPending || isPendingTransaction(transaction),
    isNoValidationKeyAlertEnabled,
    overrideValidationKey,
    selectedAddress,
    selectedAddressId,
    suggestedAddresses,
    userHasBadAddress,
    vapServiceFormFields,
  };
};
